import { useState, useCallback, useEffect, useRef } from 'react';

interface UseEditModeReturn {
  isEditMode: boolean;
  toggleEditMode: () => void;
  exitEditMode: () => void;
  selectedIndices: Set<number>;
  toggleIndex: (index: number) => void;
  selectAll: (count: number) => void;
  deselectAll: () => void;
  deleteSelected: () => Promise<void>;
  isDeleting: boolean;
}

export function useEditMode(
  onDelete: (index: number) => Promise<void>,
  itemCount: number,
  onBatchDelete?: (indices: number[]) => Promise<void>,
): UseEditModeReturn {
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedIndices, setSelectedIndices] = useState<Set<number>>(new Set());
  const [isDeleting, setIsDeleting] = useState(false);
  const deletingRef = useRef(false);

  // Escape key exits edit mode
  useEffect(() => {
    if (!isEditMode) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !deletingRef.current) {
        setIsEditMode(false);
        setSelectedIndices(new Set());
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isEditMode]);

  // Prune stale indices when itemCount shrinks
  useEffect(() => {
    setSelectedIndices(prev => {
      let changed = false;
      const next = new Set<number>();
      prev.forEach(i => {
        if (i < itemCount) {
          next.add(i);
        } else {
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [itemCount]);

  const toggleEditMode = useCallback(() => {
    if (deletingRef.current) return;
    setIsEditMode(prev => {
      if (prev) setSelectedIndices(new Set());
      return !prev;
    });
  }, []);

  const exitEditMode = useCallback(() => {
    if (deletingRef.current) return;
    setIsEditMode(false);
    setSelectedIndices(new Set());
  }, []);

  const toggleIndex = useCallback((index: number) => {
    setSelectedIndices(prev => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  }, []);

  const selectAll = useCallback((count: number) => {
    const all = new Set<number>();
    for (let i = 0; i < count; i++) all.add(i);
    setSelectedIndices(all);
  }, []);

  const deselectAll = useCallback(() => {
    setSelectedIndices(new Set());
  }, []);

  const deleteSelected = useCallback(async () => {
    if (deletingRef.current || selectedIndices.size === 0) return;
    deletingRef.current = true;
    setIsDeleting(true);
    try {
      if (onBatchDelete && selectedIndices.size > 1) {
        // Batch delete all at once
        await onBatchDelete(Array.from(selectedIndices));
      } else {
        // Single delete fallback
        const sorted = Array.from(selectedIndices).sort((a, b) => b - a);
        for (const index of sorted) {
          await onDelete(index);
        }
      }
    } finally {
      deletingRef.current = false;
      setIsDeleting(false);
      setIsEditMode(false);
      setSelectedIndices(new Set());
    }
  }, [selectedIndices, onDelete, onBatchDelete]);

  return {
    isEditMode,
    toggleEditMode,
    exitEditMode,
    selectedIndices,
    toggleIndex,
    selectAll,
    deselectAll,
    deleteSelected,
    isDeleting,
  };
}
